class CoolingPadManager {
  constructor() {
    console.log('Constructor started at:', new Date().toISOString(), 'window.cartSettings:', window.cartSettings); // Log timing and raw window value
    
    // Load settings from cookie first (fast)
    const cachedSettings = this.loadCachedSettings();
    this.settings = cachedSettings || window.cartSettings || {}; // Read from DOM or cache
    
    // NEW: If no cache but window.cartSettings exists, cache it initially
    if (!cachedSettings && window.cartSettings) {
      this.updateSettingsCookie(window.cartSettings);
    }
    
    this.coolpadVariantId = null; // Will fetch dynamically
    this.freeGiftVariantId = null; // Will fetch dynamically
    this.freeGiftThreshold = (this.settings.free_gift_eligibility_amount * 100) || 8000; // Dynamic threshold in cents
    this.hardcodedCoolpadId = 54753378697564; // Fallback if no handle
    this.hardcodedFreeGiftId = 48603432649052; // Fallback if no handle
    this.initialized = false;
    this.processing = false;
    this.lastErrorTime = 0;
    this.debounceTimer = null;
    
    // Watch for window.cartSettings changes and update cache
    this.watchForSettingsChanges();
    
    this.init();
  }

  // Load settings from cookie (with expiration check)
  loadCachedSettings() {
    const cached = document.cookie.split('; ').find(row => row.startsWith('cartSettings='));
    if (!cached) return null;
    
    try {
      const value = JSON.parse(decodeURIComponent(cached.split('=')[1]));
      const ttl = 30 * 24 * 60 * 60 * 1000; // 30 days in ms
      if (value.timestamp && Date.now() - value.timestamp < ttl) {
        console.log('Loaded cached cartSettings at:', new Date().toISOString());
        return value.data;
      }
      // Expired: Clear cookie
      this.updateSettingsCookie({});
    } catch (error) {
      console.error('Error parsing cached settings:', error);
      this.updateSettingsCookie({}); // Clear invalid cookie
    }
    return null;
  }

  // Update cookie with settings (called when window.cartSettings changes or initial cache)
  updateSettingsCookie(newSettings) {
    if (Object.keys(newSettings).length === 0) {
      document.cookie = 'cartSettings=; max-age=0; path=/'; // Clear
      console.log('Cleared cartSettings cookie at:', new Date().toISOString());
      return;
    }
    const value = encodeURIComponent(JSON.stringify({
      data: newSettings,
      timestamp: Date.now()
    }));
    document.cookie = `cartSettings=${value}; max-age=${30*24*60*60}; path=/; secure; samesite=strict`;
    console.log('Updated cartSettings cookie at:', new Date().toISOString());
  }

  // Poll or listen for window.cartSettings changes (since it's global)
  watchForSettingsChanges() {
    let lastSettings = JSON.stringify(this.settings);
    const interval = setInterval(() => {
      if (window.cartSettings && JSON.stringify(window.cartSettings) !== lastSettings) {
        console.log('window.cartSettings changed at:', new Date().toISOString());
        this.settings = { ...this.settings, ...window.cartSettings }; // Merge
        lastSettings = JSON.stringify(this.settings);
        
        // Update threshold and re-init if needed
        this.freeGiftThreshold = (this.settings.free_gift_eligibility_amount * 100) || 8000;
        this.updateSettingsCookie(this.settings); // Cache new version
        
        // Re-run logic if initialized
        if (this.initialized) {
          this.debounceEmitCartChanges();
        }
      }
    }, 500); // Check every 500ms; clear on destroy if needed
    
    // Optional: Clear interval on page unload
    window.addEventListener('beforeunload', () => clearInterval(interval));
  }

  async init() {
    console.log('init() started at:', new Date().toISOString());
    this.storedCart = this.getStoredCart() || { items: [] };
    
    // Fetch variant IDs dynamically if enabled and handles provided
    if (this.settings.enable_cooling_pad && this.settings.cooling_pad_product) {
      try {
        this.coolpadVariantId = await this.getVariantId(this.settings.cooling_pad_product);
      } catch (err) {
        console.error('Failed to fetch cooling pad variant ID:', err);
        this.coolpadVariantId = this.hardcodedCoolpadId; // Fallback
      }
    } else {
      this.coolpadVariantId = this.hardcodedCoolpadId; // Fallback if disabled or no handle
    }

    if (this.settings.enable_free_gift && this.settings.free_gift_product) {
      try {
        this.freeGiftVariantId = await this.getVariantId(this.settings.free_gift_product);
      } catch (err) {
        console.error('Failed to fetch free gift variant ID:', err);
        this.freeGiftVariantId = this.hardcodedFreeGiftId; // Fallback
      }
    } else {
      this.freeGiftVariantId = this.hardcodedFreeGiftId; // Fallback if disabled or no handle
    }

    this.observeCartChanges();
    await this.debounceEmitCartChanges(); // Debounced initial call
    this.initialized = true;
    console.log('init() completed at:', new Date().toISOString(), 'Threshold after init:', this.freeGiftThreshold);
  }

  // Helper to fetch default variant ID from product handle
  async getVariantId(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) {
      throw new Error(`Failed to fetch product: ${response.statusText}`);
    }
    const product = await response.json();
    if (!product.variants || product.variants.length === 0) {
      throw new Error(`No variants found for handle: ${handle}`);
    }
    return product.variants[0].id; // Default/first variant
  }

  getStoredCart() {
    const cartData = localStorage.getItem('cart');
    return cartData ? JSON.parse(cartData) : null;
  }

  storeCart(cart) {
    localStorage.setItem('cart', JSON.stringify(cart));
    this.storedCart = cart;
  }

  async fetchCart() {
    const response = await this.safeFetch('/cart.js');
    return response.json();
  }

  // Safe fetch with retry on 429 and non-JSON handling
  async safeFetch(url, options = {}, retries = 3, delay = 1000) {
    for (let i = 0; i < retries; i++) {
      const response = await fetch(url, options);
      if (response.status === 429) {
        console.warn(`Rate limited (429) on ${url}. Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        delay *= 2; // Exponential backoff
        continue;
      }
      if (!response.ok) {
        let errorText;
        try {
          errorText = await response.json();
        } catch {
          errorText = await response.text(); // For non-JSON (e.g., HTML)
        }
        this.lastErrorTime = Date.now();
        throw new Error(`Failed request to ${url}: ${JSON.stringify(errorText)}`);
      }
      return response;
    }
    throw new Error(`Max retries exceeded for ${url}`);
  }

  // NEW: Disable/enable checkout button
  toggleCheckoutButton(disabled) {
    // Find checkout buttons (support multiple, e.g., sticky and main)
    const buttons = document.querySelectorAll('button[name="checkout"], .cart__checkout-button, #checkout'); // Adjusted to match your button (id="checkout", class="cart__checkout-button", name="checkout")
    buttons.forEach(button => {
      button.disabled = disabled;
      if (disabled) {
        button.classList.add('disabled'); // Optional: Add class for styling (e.g., opacity-50 cursor-not-allowed)
        button.textContent = 'Processing...'; // Optional: Change text during disable
      } else {
        button.classList.remove('disabled');
        button.textContent = 'Auschecken'; // Restore original text (from your HTML)
      }
    });
    console.log(disabled ? 'Checkout button disabled' : 'Checkout button enabled');
  }

  observeCartChanges() {
    this.cartObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
        const isCartChangeRequest = /\/cart\/(add|change|update|clear)/.test(entry.name);
        if (isValidRequestType && isCartChangeRequest) {
          this.debounceEmitCartChanges();
        }
      });
    });
    this.cartObserver.observe({ entryTypes: ["resource"] });
  }

  debounceEmitCartChanges() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.emitCartChanges(), 500);
  }

  async emitCartChanges() {
    if (this.processing) return;
    if (Date.now() - this.lastErrorTime < 30000) {
      console.warn('Skipping emitCartChanges due to recent error.');
      return;
    }

    this.processing = true;
    this.toggleCheckoutButton(true); // Disable checkout at start
    if (this.cartObserver) this.cartObserver.disconnect(); // Prevent loop

    try {
      const previousCart = await this.fetchCart();
      const previousSpecialItems = this.extractSpecialItemsSnapshot(previousCart);

      let currentCart = previousCart;

      // Coolpad logic only if enabled
      if (this.settings.enable_cooling_pad) {
        await this.handleCoolpadLogic(currentCart);
        currentCart = await this.fetchCart();
      }

      // Free gift logic only if enabled
      if (this.settings.enable_free_gift) {
        await this.handleFreeGiftLogic(currentCart);
        currentCart = await this.fetchCart();
      }

      await this.removeSpecialItemsIfAlone(currentCart);
      currentCart = await this.fetchCart();

      const currentSpecialItems = this.extractSpecialItemsSnapshot(currentCart);

      this.storeCart(currentCart);

      if (!this.areSpecialItemsEqual(previousSpecialItems, currentSpecialItems)) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error processing cart:', error);
      this.lastErrorTime = Date.now();
    } finally {
      this.processing = false;
      this.toggleCheckoutButton(false); // Re-enable checkout at end
      if (this.cartObserver) this.cartObserver.observe({ entryTypes: ["resource"] }); // Reconnect
    }
  }

  extractSpecialItemsSnapshot(cart) {
    return cart.items
      .filter(item => 
        item.variant_id === this.coolpadVariantId ||
        (item.variant_id === this.freeGiftVariantId && item.properties?._free_gift)
      )
      .map(item => ({
        variant_id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {}
      }));
  }
  
  areSpecialItemsEqual(before, after) {
    const sortedBefore = [...before].sort((a, b) => a.variant_id - b.variant_id);
    const sortedAfter = [...after].sort((a, b) => a.variant_id - b.variant_id);
    return JSON.stringify(sortedBefore) === JSON.stringify(sortedAfter);
  }

  async removeSpecialItemsIfAlone(cart) {
    const containsOnlySpecialItems = cart.items.every(item => 
      item.variant_id === this.coolpadVariantId || 
      (item.variant_id === this.freeGiftVariantId && 
       item.properties?._free_gift)
    );

    if (containsOnlySpecialItems && cart.items.length > 0) {
      await this.removeAllSpecialItems(cart);
    }
  }

  async removeAllSpecialItems(cart) {
    const updates = {};
    
    cart.items.forEach(item => {
      if (item.variant_id === this.coolpadVariantId || 
          (item.variant_id === this.freeGiftVariantId && 
           item.properties?._free_gift)) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) return;

    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await this.safeFetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return response.json();
  }

  async handleCoolpadLogic(newCart) {
    const requiresCooling = this.checkCartForCooling(newCart);
    const coolpadItems = newCart.items.filter(item => item.variant_id === this.coolpadVariantId);
    const hasCoolpad = coolpadItems.length > 0;
    
    const coolpadQuantity = coolpadItems.reduce((sum, item) => sum + item.quantity, 0);

    if (requiresCooling) {
      if (!hasCoolpad) {
        await this.addCoolpad();
      } else if (coolpadQuantity !== 1) {
        await this.adjustCoolpadQuantity(coolpadItems);
      }
    } else {
      if (hasCoolpad) {
        await this.removeCoolpadItems(newCart);
      }
    }
  }

  async handleFreeGiftLogic(newCart) {
    // Filter valid free gifts: exact current variant ID + _free_gift property
    const validGiftItems = newCart.items.filter(item => 
      item.variant_id === this.freeGiftVariantId && 
      item.properties && 
      (item.properties._free_gift === true || item.properties._free_gift === "true")
    );

    // Identify invalid "free" items (marked as free but wrong variant ID, e.g., old gift)
    const invalidGiftItems = newCart.items.filter(item => 
      item.variant_id !== this.freeGiftVariantId && 
      item.properties && 
      (item.properties._free_gift === true || item.properties._free_gift === "true")
    );

    const giftLinePriceSum = validGiftItems.reduce((sum, item) => sum + item.line_price, 0);
    const baseTotal = newCart.total_price - giftLinePriceSum;
    console.log('In handleFreeGiftLogic at:', new Date().toISOString(), 'Base total (cents):', baseTotal, 'Threshold:', this.freeGiftThreshold, 'Qualifies:', baseTotal > this.freeGiftThreshold);
    const qualifiesForGift = baseTotal > this.freeGiftThreshold;
    const hasValidGift = validGiftItems.length > 0;

    // Always remove invalid free gifts (old ones)
    if (invalidGiftItems.length > 0) {
      await this.removeInvalidFreeGifts(invalidItems);
    }

    if (qualifiesForGift) {
      if (!hasValidGift) {
        // Add the current one if none valid
        await this.addFreeGift();
      } else {
        // Enforce exactly one for the valid one
        const validGiftQuantity = validGiftItems.reduce((sum, item) => sum + item.quantity, 0);
        if (validGiftQuantity !== 1) {
          await this.adjustFreeGiftQuantity(validGiftItems);
        }
      }
    } else {
      // Remove all free gifts (valid and invalid already handled above)
      if (hasValidGift) {
        await this.removeFreeGiftItems(newCart);
      }
    }
  }

  // Remove invalid free gifts (wrong variant but marked free)
  async removeInvalidFreeGifts(invalidItems) {
    const updates = {};
    invalidItems.forEach(item => {
      updates[item.key] = 0;
    });

    if (Object.keys(updates).length === 0) return;

    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await this.safeFetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return response.json();
  }

  // Adjust free gift quantity to exactly 1 (similar to coolpad)
  async adjustFreeGiftQuantity(giftItems) {
    const updates = {};
    let foundFirst = false;
    
    giftItems.forEach(item => {
      if (!foundFirst) {
        // Set first to quantity 1
        updates[item.key] = 1;
        foundFirst = true;
      } else {
        // Remove extras
        updates[item.key] = 0;
      }
    });
    
    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await this.safeFetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return response.json();
  }

  async adjustCoolpadQuantity(coolpadItems) {
    const updates = {};
    let foundFirst = false;
    
    coolpadItems.forEach(item => {
      if (!foundFirst) {
        updates[item.key] = 1;
        foundFirst = true;
      } else {
        updates[item.key] = 0;
      }
    });
    
    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await this.safeFetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return response.json();
  }

  checkCartForCooling(cart) {
    return cart.items.some(item => {
      if (item.variant_id === this.coolpadVariantId) return false;
      if (item.properties && item.properties._requires_cooling) {
        const coolingValue = item.properties._requires_cooling;
        return coolingValue === true || coolingValue === "true";
      }
      return false;
    });
  }

  async addCoolpad() {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await this.safeFetch(`${rootUrl}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: this.coolpadVariantId, quantity: 1 }] })
    });
    return response.json();
  }

  async addFreeGift() {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await this.safeFetch(`${rootUrl}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: this.freeGiftVariantId, quantity: 1, properties: { _free_gift: true } }] })
    });
    return response.json();
  }

  async removeCoolpadItems(cart) {
    const coolpadItems = cart.items.filter(item => item.variant_id === this.coolpadVariantId);
    if (coolpadItems.length === 0) return;

    const updates = {};
    coolpadItems.forEach(item => updates[item.key] = 0);

    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await this.safeFetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return response.json();
  }

  async removeFreeGiftItems(cart) {
    const giftItems = cart.items.filter(item => 
      item.properties && 
      (item.properties._free_gift === true || item.properties._free_gift === "true")
    ); // Remove all marked as free, regardless of variant (cleans old ones too)
    if (giftItems.length === 0) return;

    const updates = {};
    giftItems.forEach(item => updates[item.key] = 0);

    const rootUrl = window.Shopify?.routes?.root || '/';
    const response = await this.safeFetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return response.json();
  }
}
console.log('Before CoolingPadManager init, cartSettings available:', window.cartSettings); // Check if settings are ready
// Initialize the manager
if (typeof window.coolingPadManager === 'undefined') {
  window.coolingPadManager = new CoolingPadManager();
}